// ----------------------------------------------------------------------
// Answering "Create a BeastOS account" after a Google or Apple sign-in that
// no console account uses.
//
// The backend made nothing when the sign-in finished and handed back a
// ticket. It is good only in the browser the sign-in was finished in, so it is
// answered here, on the console's own origin and never on
// `VITE_BACKOFFICE_URL`, where the HttpOnly cookie that says which browser
// this is goes with the request, as it does for the trips themselves.
// ----------------------------------------------------------------------

export type NewAccountOutcome =
  | { token: string; invitation: 'accepted' | 'unavailable' | null }
  /** An account took the address meanwhile, and it has an authenticator. */
  | { secondFactorTicket: string }

/** A refusal from the backend, with its code and what it said. */
export class NewAccountError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'NewAccountError'
  }
}

const UNREACHABLE =
  'The account could not be made. Check your connection and try again.'

const CONFIRM = `mutation AuthNewAccountConfirm($input: MutationAuthNewAccountConfirmInput!) {
  authNewAccountConfirm(input: $input) {
    token
    invitation
    secondFactorTicket
  }
}`

interface Answer {
  data?: {
    authNewAccountConfirm?: {
      token: string | null
      invitation: 'accepted' | 'unavailable' | null
      secondFactorTicket: string | null
    } | null
  } | null
  errors?: { message?: string; extensions?: { code?: string } }[]
}

/** Makes the account the ticket was held for, and signs in to it. */
export const newAccountConfirm = async (
  newAccountTicket: string,
): Promise<NewAccountOutcome> => {
  let response: Response
  try {
    response = await fetch(`${window.location.origin}/backoffice/graphql`, {
      method: 'POST',
      // The default, stated: the cookie is this origin's and goes only here.
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        query: CONFIRM,
        variables: { input: { newAccountTicket } },
      }),
    })
  } catch {
    throw new NewAccountError('UNREACHABLE', UNREACHABLE)
  }

  const answer = (await response.json().catch(() => ({}))) as Answer
  const [error] = answer.errors ?? []
  const confirmed = answer.data?.authNewAccountConfirm
  if (error || !confirmed) {
    throw new NewAccountError(
      error?.extensions?.code ?? 'UNKNOWN',
      error?.message ?? UNREACHABLE,
    )
  }

  if (confirmed.secondFactorTicket) {
    return { secondFactorTicket: confirmed.secondFactorTicket }
  }
  if (!confirmed.token) {
    throw new NewAccountError('UNKNOWN', UNREACHABLE)
  }
  return { token: confirmed.token, invitation: confirmed.invitation }
}
